import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Button,
  focusTrap,
  ProfilePhoto,
  TranslationProvider,
} from '@resy/ui-kitchen';
import { loadResyConfigThunk } from '../../../../../../../ui-kitchen/src/reducers';
import { IDLE } from '../../../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import Menu from '../../../Menu';
import menuItemsConfig from '../../../../containers/MenuContainer/menuItems';
import './AmexShopSmallHeaderLogin.scss';
import Close from '../../../../svg/dynamic/close.svg';
import {
  getUserThunk, selectIsConcierge, selectIsGlobalDiningAccess, userLogoutThunk,
} from '../../../../../../../ui-kitchen/src/reducers/userSlice';

const AmexShopSmallHeaderLogin = (
  {
    $rootScope,
    Analytics,
    isAuthenticated,
    matchMedia,
    tagConfig,
    configStatus,
    dispatch,
    isConcierge,
    isGdaEnabled,
    isGda,
    profilePhotoUrl,
  }
) => {
  const {
    login: loginEventName,
    category: loginCategory,
    label: loginLabel,
  } = tagConfig?.header;
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [dropdowns, setDropdowns] = useState({
    menu: false,
  });
  const profileMenuRef = useRef();

  const closeEverything = () => {
    const clonedDropdowns = dropdowns;
    Object.keys(clonedDropdowns).forEach((key) => { clonedDropdowns[key] = false; });
    setIsBackdropOpen(false);
    setDropdowns((prevState) => ({
      ...prevState,
      ...clonedDropdowns,
    }));
    focusTrap.focusRemove();
    $rootScope.isBackdropOpen = false;
    $rootScope.$digest();
  };

  const generateMenuItems = async () => {
    let clonedMenuItems = [];

    if (!matchMedia.md) {
      if (!isAuthenticated) {
        clonedMenuItems = [
          ...(isGdaEnabled ? menuItemsConfig.gda : []),
          ...menuItemsConfig.loggedOut,
        ];
      } else {
        clonedMenuItems = [
          ...(isConcierge ? menuItemsConfig.concierge : []),
          ...menuItemsConfig.loggedIn,
          { key: 'dividerKey', name: 'divider' },
          ...(isGdaEnabled ? menuItemsConfig.gda : []),
          ...menuItemsConfig.loggedOut,
        ];
      }
    } else {
      clonedMenuItems = [
        ...(isConcierge ? menuItemsConfig.concierge : []),
        ...menuItemsConfig.loggedIn,
        { key: 'dividerKey', name: 'divider' },
      ];
    }
    setMenuItems(clonedMenuItems);
  };

  const onDropdownClick = (dropdown) => {
    const clonedDropdowns = dropdowns;
    Object.keys(clonedDropdowns).forEach((key) => {
      if (key === dropdown) {
        const shouldOpen = !clonedDropdowns[key];
        clonedDropdowns[key] = shouldOpen;
        setIsBackdropOpen(shouldOpen);
        setDropdowns((prevState) => ({
          ...prevState,
          ...clonedDropdowns,
        }));
        $rootScope.isBackdropOpen = shouldOpen;

        if (shouldOpen) {
          if (dropdown === 'menu') {
            if (matchMedia.md) {
              focusTrap.focusModal(
                '.AmexShopSmallMenuContainer__menu-and-auth-button'
              );
            }
          }
          $rootScope.$digest();

          Analytics.event('Dropdown opened', {
            category: 'Nav',
            label: dropdown,
          });
        } else {
          focusTrap.focusRemove();
          $rootScope.$digest();
        }
      }
    });
  };

  const handleClickOutside = (e) => {
    if (profileMenuRef?.current?.contains(e.target)) {
      closeEverything();
    }
  };

  const toggleMenu = () => {
    onDropdownClick('menu');
    $rootScope.$evalAsync();
  };

  useEffect(() => {
    // clean up controller
    const loadUser = () => dispatch(getUserThunk(true));
    closeEverything();
    loadUser();
    generateMenuItems();
    document.addEventListener('mousedown', handleClickOutside);
    // cancel subscription to useEffect
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      generateMenuItems();
      closeEverything();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    generateMenuItems();
  }, [isGdaEnabled]);

  useEffect(() => {
    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
  }, [configStatus]);

  const onLoginShopSmall = () => {
    Analytics.event(loginEventName, {
      category: loginCategory,
      label: loginLabel,
    });
    $rootScope.$broadcast('MODAL', 'signin');
  };

  const onLogout = () => {
    dispatch(userLogoutThunk());
    closeEverything();
  };

  const renderMenuAndAuthButtonForMobile = () => (
    isAuthenticated ? (
      <div
        className={`AmexShopSmallMenuContainer__menu-and-auth-button
        ${dropdowns.menu ? 'AmexShopSmallMenuContainer__menu-and-auth-button--open' : ''}`}
      >
        <Menu Analytics={Analytics} items={menuItems} />
        <div className="AmexShopSmallMenuContainer__auth-button-container logged-in">
          <TranslationProvider translationKey="signout">
            <Button
              variant="no-style"
              onClick={onLogout}
              data-test-id="menu_container-button-sign_out"
            >
              Sign out
            </Button>
          </TranslationProvider>
        </div>
      </div>
    ) : (
      <div
        className={`AmexShopSmallMenuContainer__menu-and-auth-button
          ${dropdowns.menu ? 'AmexShopSmallMenuContainer__menu-and-auth-button--open' : ''}`}
      >
        <Menu Analytics={Analytics} items={menuItems} />
        <div className="AmexShopSmallMenuContainer__auth-button-container logged-out">
          <TranslationProvider translationKey="login">
            <Button
              variant="login"
              onClick={onLoginShopSmall}
              data-test-id="menu_container-button-login"
            >
              Log in
            </Button>
          </TranslationProvider>
        </div>
      </div>
    )
  );

  const renderMenuAndAuthButtonForDesktop = () => (
    <div className="AmexShopSmallMenuContainer__menu-and-auth-button">
      <Button
        aria-label="Close Profile menu"
        variant="close"
        className="AmexShopSmallMenuContainer__close-container"
        onClick={closeEverything}
        data-test-id="menu_container-button-close"
      />
      <Menu Analytics={Analytics} items={menuItems} />
      <div className="AmexShopSmallMenuContainer__auth-button-container logged-in">
        <TranslationProvider translationKey="signout">
          <Button
            variant="no-style"
            onClick={onLogout}
            data-test-id="menu_container-button-logout"
          >
            Sign out
          </Button>
        </TranslationProvider>
      </div>
    </div>
  );

  const renderMobile = () => (
    <>
      {!isAuthenticated && (
        <TranslationProvider translationKey="login">
          <Button
            variant="login"
            onClick={onLoginShopSmall}
            data-test-id="menu_container-button-log_in"
          >
            Log in
          </Button>
        </TranslationProvider>
      )}

      {isAuthenticated && (
        <>
          {dropdowns.menu ? (
            <Button
              className="ResyNavShopSmall__close-icon"
              name="close"
              variant="close"
              onClick={closeEverything}
              aria-label="Close Profile menu"
            >
              <Close />
            </Button>
          ) : (
            <Button
              aria-expanded={dropdowns.menu}
              variant="no-style"
              className={`ResyIcon__container ${isGda ? 'ResyIcon__container--rga' : ''}`}
              onClick={toggleMenu}
              data-test-id="menu_container-button-profile_photo"
              data-dd-privacy="mask"
              aria-label="Profile menu"
            >
              <ProfilePhoto
                isGda={isGda}
                url={profilePhotoUrl}
              />
            </Button>
          )}
        </>
      )}
      {isAuthenticated && dropdowns.menu && renderMenuAndAuthButtonForMobile()}
    </>
  );

  const renderDesktop = () => (
    <>

      {!isAuthenticated && (
        <TranslationProvider translationKey="login">
          <Button
            variant="login"
            onClick={onLoginShopSmall}
            data-test-id="menu_container-button-log_in"
          >
            Log in
          </Button>
        </TranslationProvider>
      )}
      {isAuthenticated && (
        <Button
          aria-expanded={dropdowns.menu}
          variant="no-style"
          className={`ResyIcon__container ${isGda ? 'ResyIcon__container--rga' : ''}`}
          onClick={toggleMenu}
          aria-label="Profile menu"
          data-test-id="menu_container-button-profile_photo"
          data-dd-privacy="mask"
        >
          <ProfilePhoto
            isGda={isGda}
            url={profilePhotoUrl}
          />
        </Button>
      )}
      {isAuthenticated && dropdowns.menu && renderMenuAndAuthButtonForDesktop()}
    </>
  );

  const containerClasses = [
    'AmexShopSmallMenuContainer',
    matchMedia.md ? 'AmexShopSmallMenuContainer--desktop' : 'AmexShopSmallMenuContainer--mobile',
    isAuthenticated ? 'auth' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        className={containerClasses}
      >
        {matchMedia.md ? renderDesktop() : renderMobile()}
      </div>
      {matchMedia.md && (
        <div
          className={`ResyNavShopSmall__backdrop ${isBackdropOpen ? 'ResyNavShopSmall__backdrop--open' : ''}`}
          ref={profileMenuRef}
        />
      )}
    </>
  );
};

AmexShopSmallHeaderLogin.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    $digest: PropTypes.func,
    $evalAsync: PropTypes.func,
    $on: PropTypes.func,
    isBackdropOpen: PropTypes.bool,
    nav: PropTypes.shape({}),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  tagConfig: PropTypes.shape({
    header: PropTypes.shape({}),
  }).isRequired,
  configStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  isConcierge: PropTypes.bool.isRequired,
  isGdaEnabled: PropTypes.bool,
  isGda: PropTypes.bool.isRequired,
  profilePhotoUrl: PropTypes.string,
};

AmexShopSmallHeaderLogin.defaultProps = {
  isGdaEnabled: false,
  profilePhotoUrl: null,
};

const mapStateToProps = (state) => {
  const { resyConfig, user } = state;
  return ({
    configStatus: resyConfig.configStatus,
    isConcierge: selectIsConcierge(state),
    isGdaEnabled: resyConfig.config?.enable_global_dining_access,
    isGda: selectIsGlobalDiningAccess(state),
    profilePhotoUrl: user.userData?.profile_image_url,
  });
};

export { AmexShopSmallHeaderLogin as ComponentUnderTest };
export default connect(mapStateToProps)(AmexShopSmallHeaderLogin);
